/**
 * O cartão de tarefa que expande no lugar (H10).
 *
 * ── Por que expande, e não navega ─────────────────────────────────────────
 *
 * A tarefa não tem tela própria — é curta demais para uma. Abrir uma página
 * para ler três linhas de descrição e voltar custa dois carregamentos e perde
 * o lugar na lista. O cartão abre aqui, e a lista em volta continua sendo o
 * contexto: "o que mais eu tenho para hoje".
 *
 * É `<details>/<summary>` nativo: teclado e leitor de tela saem de graça.
 *
 * ── A caixa de concluir fica FORA do `<summary>` ──────────────────────────
 *
 * Controle dentro de `summary` disputa o clique de abrir, e o gesto mais
 * frequente da tela não pode depender de a pessoa acertar o meio da caixa. Ela
 * é IRMÃ do `<details>`, dentro do cartão — a ordem dos filhos importa.
 *
 * ── O `open` é da APLICAÇÃO, e não do DOM ─────────────────────────────────
 *
 * Sem isso, um render disparado por qualquer outro motivo — a conclusão de
 * outra tarefa, o SSE, uma revalidação — fecharia o cartão que a pessoa está
 * lendo no meio da leitura.
 *
 * ── O corpo só chega ao ABRIR ─────────────────────────────────────────────
 *
 * `GET /v1/tasks` é servida da projeção (D01), que não guarda o corpo. Quem o
 * tem é `GET /v1/tasks/{id}`, que vai ao motor. Uma ida por cartão aberto é
 * barata; trinta para desenhar a lista, não — e é por isso que a descrição
 * aparece depois de o cartão abrir, e não antes.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { PrioridadeDaTarefa, TarefaDaApi } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { useApi, useResponsaveisDeTarefa, useTarefa } from '@/shared/api/consultas';
import { useInvalidar } from '@/shared/api/provedor';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { prazoEmPalavras } from '@/entities/comercial/tarefa';
import { Avatar, Botao, Campo, CampoArea, CampoSelecao, Cartao, Icone } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { PRIORIDADES, pastilhaDePrioridade } from '@/entities/comercial/tarefa';

const NOMES_DE_ORIGEM: Record<string, string> = {
  manual: 'criada à mão',
  automacao: 'criada por automação',
  sla: 'criada por regra de SLA',
  conector: 'criada por integração de portal',
};

export function CartaoDeTarefa({
  tarefa,
  concluida,
  aberta,
  aoAlternar,
  aoConcluir,
  aoAbrirConversa,
  aoAbrirCliente,
}: {
  tarefa: TarefaDaApi;
  concluida: boolean;
  aberta: boolean;
  aoAlternar: (aberta: boolean) => void;
  aoConcluir: (tarefa: TarefaDaApi) => void;
  aoAbrirConversa: (conversaId: string) => void;
  aoAbrirCliente: (clienteId: string) => void;
}): ReactNode {
  const [editando, definirEditando] = useState(false);
  const prazo = prazoEmPalavras(tarefa.prazo_em);
  const pastilha = pastilhaDePrioridade(tarefa.prioridade);

  /*
   * `id === null` é a tarefa nascida dentro do Twenty: a projeção a traz, mas
   * ela nunca passou pela API e não tem identificador nosso. Sem ele não há o
   * que pedir, concluir, alterar ou apagar.
   */
  const temCanonico = tarefa.id !== null;

  return (
    <li>
      <Cartao
        parte={tarefa.atrasada && !concluida ? 'tar__cartao tar__cartao--atrasada' : 'tar__cartao'}
        data-concluida={concluida ? 'sim' : 'nao'}
      >
        {/* IRMÃ do `<details>`, nunca filha do `<summary>`. */}
        <button
          type="button"
          className="tar__marcar"
          aria-pressed={concluida}
          aria-label={concluida ? `Concluída: ${tarefa.titulo}` : `Concluir: ${tarefa.titulo}`}
          disabled={!temCanonico || concluida}
          title={
            temCanonico
              ? undefined
              : 'Criada no CRM: para concluir por aqui, ela precisa ter passado pela API'
          }
          onClick={() => aoConcluir(tarefa)}
        >
          {concluida ? '✓' : null}
        </button>

        <details
          className="tar__item"
          open={aberta}
          onToggle={(evento) => aoAlternar(evento.currentTarget.open)}
        >
          <summary className="tar__resumo">
            <span className="tar__resumo-texto">
              <span className="tar__nome">{tarefa.titulo}</span>
              <span className="tar__meta">
                {prazo && (
                  <span className={prazo.passou && !concluida ? 'tar__prazo--atraso' : undefined}>
                    {prazo.texto}
                  </span>
                )}
                {tarefa.origem && (
                  <span title={tarefa.origem.motivo ?? undefined}>
                    {NOMES_DE_ORIGEM[tarefa.origem.tipo] ?? tarefa.origem.tipo}
                  </span>
                )}
                {tarefa.oportunidade && (
                  <span className="tar__oportunidade">
                    <span className="visualmente-oculto">Oportunidade: </span>
                    {tarefa.oportunidade.titulo ?? 'oportunidade sem nome'}
                  </span>
                )}
                {!temCanonico && <span className="tar__so-engine">criada no CRM</span>}
              </span>
            </span>

            <span className="tar__direita">
              {pastilha && <span className={pastilha.classe}>{pastilha.nome}</span>}
              <Responsavel responsavel={tarefa.responsavel} />
              <span className="tar__chevron" aria-hidden="true">
                <Icone nome="direita" />
              </span>
            </span>
          </summary>

          {/*
            O detalhe só é MONTADO quando o cartão abre: é o que faz a busca do
            corpo acontecer por cartão aberto, e não por lista carregada.
          */}
          {aberta &&
            (editando ? (
              <FormularioDaTarefa tarefa={tarefa} aoSair={() => definirEditando(false)} />
            ) : (
              <Detalhe
                tarefa={tarefa}
                concluida={concluida}
                podeAgir={temCanonico}
                aoConcluir={() => aoConcluir(tarefa)}
                aoEditar={() => definirEditando(true)}
              />
            ))}
        </details>

        {/*
          O cliente fica FORA do `<summary>`, no pé do cartão: um botão dentro
          dele seria controle dentro de controle, e o leitor de tela anuncia os
          dois como um só.
        */}
        {tarefa.cliente && (
          <Cliente
            cliente={tarefa.cliente}
            aoAbrirConversa={aoAbrirConversa}
            aoAbrirCliente={aoAbrirCliente}
          />
        )}
      </Cartao>
    </li>
  );
}

/**
 * De quem é a tarefa (#950): o disco com as iniciais e o nome do cliente.
 *
 * O nome é BOTÃO, e leva à conversa — quem lê uma tarefa de cliente vai falar
 * com ele. Cliente ainda sem conversa abre o Cliente 360.
 */
function Cliente({
  cliente,
  aoAbrirConversa,
  aoAbrirCliente,
}: {
  cliente: NonNullable<TarefaDaApi['cliente']>;
  aoAbrirConversa: (conversaId: string) => void;
  aoAbrirCliente: (clienteId: string) => void;
}): ReactNode {
  const nome = cliente.nome_exibicao ?? 'Cliente sem nome';
  const destino = cliente.conversa_id ? 'Abrir a conversa' : 'Abrir o Cliente 360';
  return (
    <button
      type="button"
      className="tar__cliente"
      title={`${destino} de ${nome}`}
      aria-label={`${destino} de ${nome}`}
      onClick={() =>
        cliente.conversa_id ? aoAbrirConversa(cliente.conversa_id) : aoAbrirCliente(cliente.id)
      }
    >
      <Avatar nome={cliente.nome_exibicao} semente={cliente.id} tamanho={26} />
      <span className="tar__cliente-nome">{nome}</span>
    </button>
  );
}

/**
 * Quem responde pela tarefa, no canto do cartão — foto quando há, iniciais
 * quando não, como o card do funil. Sem dono, nada: o detalhe já diz "sem
 * dono", e um disco vazio em toda tarefa da fila seria ruído.
 */
function Responsavel({ responsavel }: { responsavel: TarefaDaApi['responsavel'] }): ReactNode {
  if (!responsavel) return null;
  const dica = `Responsável: ${responsavel.nome ?? 'pessoa sem acesso ao CliqCRM'}`;
  return (
    <span className="tar__dono" title={dica}>
      <Avatar nome={responsavel.nome} tamanho={28} foto={responsavel.foto_url} />
      <span className="visualmente-oculto">{dica}</span>
    </span>
  );
}

/** O detalhe em leitura: descrição, vínculos e as três ações. */
function Detalhe({
  tarefa,
  concluida,
  podeAgir,
  aoConcluir,
  aoEditar,
}: {
  tarefa: TarefaDaApi;
  concluida: boolean;
  podeAgir: boolean;
  aoConcluir: () => void;
  aoEditar: () => void;
}): ReactNode {
  const consulta = useTarefa(tarefa.id ?? '');
  const equipe = useResponsaveisDeTarefa();
  const api = useApi();
  const invalidar = useInvalidar();
  const [erro, definirErro] = useState<string | null>(null);
  const [confirmando, definirConfirmando] = useState(false);

  // O corpo vem da busca por id; o resto já está na linha e não precisa
  // esperar a rede para aparecer.
  const completa = consulta.data ?? tarefa;
  const prazo = prazoEmPalavras(completa.prazo_em);
  const responsavel = (equipe.data?.itens ?? []).find(
    (pessoa) => pessoa.referencia_externa === completa.responsavel_referencia_externa,
  );

  const apagar = useMutation({
    mutationFn: async () => {
      if (!api || !tarefa.id) throw new Error('Sem sessão.');
      definirErro(null);
      await api.removerTarefa({ caminho: { id: tarefa.id } });
    },
    onSuccess: () => invalidar([chaves.tarefas(), chaves.timeline()]),
    onError: (e: unknown) =>
      definirErro(recadoDeErro(e, { generica: 'Não foi possível apagar a tarefa.' })),
  });

  return (
    <div className="tar__detalhe">
      {erro && <Faixa tom="bloqueado">{erro}</Faixa>}

      {consulta.isPending && podeAgir ? (
        <p className="tar__descricao tar__descricao--vazia">Buscando a descrição…</p>
      ) : completa.corpo ? (
        <p className="tar__descricao">{completa.corpo}</p>
      ) : (
        /* A descrição vazia é DITA: um espaço em branco faria procurar onde
           clicar, e some a chance de a tarefa ser passada adiante sem
           contexto. */
        <p className="tar__descricao tar__descricao--vazia">
          Sem descrição. Editar acrescenta uma — é o que a próxima pessoa lê antes de ligar.
        </p>
      )}

      <dl className="ficha tar__vinculos">
        <dt>Prazo</dt>
        <dd>{prazo?.texto ?? 'sem prazo'}</dd>
        <dt>Responsável</dt>
        <dd>
          {responsavel?.nome ??
            (completa.responsavel_referencia_externa ? 'fora da sua carteira' : 'sem dono')}
        </dd>
      </dl>

      {confirmando ? (
        /*
          A confirmação fica NO CARTÃO, e não num diálogo.

          Apagar é físico no Twenty (ADR-0007): a tarefa some da lista e da
          linha do tempo do cliente. Perguntar aqui mantém à vista o que vai
          sumir — um diálogo por cima esconderia justamente a tarefa sobre a
          qual se está decidindo.
        */
        <div className="tar__acoes-detalhe tar__acoes-detalhe--form">
          <Botao
            variante="perigo"
            desabilitado={apagar.isPending}
            motivo="Apagando…"
            onClick={() => apagar.mutate()}
          >
            {apagar.isPending ? 'Apagando…' : 'Apagar para sempre'}
          </Botao>
          <Botao variante="fantasma" onClick={() => definirConfirmando(false)}>
            Cancelar
          </Botao>
        </div>
      ) : (
        <div className="tar__acoes-detalhe">
          <Botao
            variante="primario"
            desabilitado={!podeAgir || concluida}
            motivo={
              !podeAgir
                ? 'Criada fora do CliqCRM: não dá para editar nem concluir por aqui.'
                : 'Esta tarefa já está concluída.'
            }
            onClick={aoConcluir}
          >
            Concluir tarefa
          </Botao>
          <Botao
            variante="secundario"
            desabilitado={!podeAgir}
            motivo="Criada fora do CliqCRM: não dá para editar nem concluir por aqui."
            onClick={aoEditar}
          >
            Editar
          </Botao>
          <Botao
            variante="fantasma"
            icone
            desabilitado={!podeAgir}
            motivo="Criada fora do CliqCRM: não dá para editar nem concluir por aqui."
            aria-label={`Apagar a tarefa: ${tarefa.titulo}`}
            onClick={() => definirConfirmando(true)}
          >
            <Icone nome="lixeira" />
          </Botao>
        </div>
      )}

      {dataDaCriacao(completa.criada_em) && (
        <span className="tar__origem">Criada em {dataDaCriacao(completa.criada_em)}</span>
      )}
    </div>
  );
}

/**
 * O carimbo de criação: uma DATA, e não "há quanto tempo".
 *
 * Usar `prazoEmPalavras` aqui produzia "Criada prazo passou há 1 dia" — a
 * função é do PRAZO, e a frase dela carrega esse sentido junto. Quando a
 * tarefa foi criada é um fato de calendário, e quem lê quer a data.
 *
 * Formatada no fuso LOCAL de propósito: "8 de setembro" é o dia de quem está
 * lendo, e não o do servidor.
 */
function dataDaCriacao(instante: string | null): string | null {
  if (!instante) return null;
  const data = new Date(instante);
  if (Number.isNaN(data.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long' }).format(data);
}

/**
 * A edição NO LUGAR.
 *
 * Um diálogo por cima esconderia justamente a lista que dá contexto, e
 * reescreveria os mesmos quatro campos em outro lugar.
 *
 * Só o que MUDOU é enviado: o `PATCH` trata campo ausente como "não mexa", e
 * mandar o objeto inteiro apagaria o prazo de quem só quis trocar o título.
 */
function FormularioDaTarefa({
  tarefa,
  aoSair,
}: {
  tarefa: TarefaDaApi;
  aoSair: () => void;
}): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();
  const consulta = useTarefa(tarefa.id ?? '');
  const equipe = useResponsaveisDeTarefa();
  const completa = consulta.data ?? tarefa;

  const [titulo, definirTitulo] = useState(completa.titulo);
  const [corpo, definirCorpo] = useState(completa.corpo ?? '');
  const [prazo, definirPrazo] = useState(paraCampoLocal(completa.prazo_em));
  const [prioridade, definirPrioridade] = useState<PrioridadeDaTarefa>(completa.prioridade);
  const [responsavel, definirResponsavel] = useState(completa.responsavel_referencia_externa ?? '');

  const salvar = useMutation({
    mutationFn: async () => {
      if (!api || !tarefa.id) throw new Error('Sem sessão.');
      const mudou = diferenca(completa, {
        titulo: titulo.trim(),
        corpo: corpo.trim() === '' ? null : corpo.trim(),
        prazo_em: prazo === '' ? null : new Date(prazo).toISOString(),
        prioridade,
        responsavel_referencia_externa: responsavel === '' ? null : responsavel,
      });
      if (Object.keys(mudou).length === 0) return;
      await api.alterarTarefa({ caminho: { id: tarefa.id }, corpo: mudou });
    },
    onSuccess: () => {
      invalidar([chaves.tarefas(), chaves.tarefa(tarefa.id ?? '')]);
      aoSair();
    },
  });

  return (
    <div className="tar__detalhe">
      {salvar.error != null && (
        <Faixa tom="bloqueado">
          {recadoDeErro(salvar.error, { generica: 'Não foi possível salvar a tarefa.' })}
        </Faixa>
      )}

      <form
        className="tar__form"
        onSubmit={(evento: FormEvent) => {
          evento.preventDefault();
          salvar.mutate();
        }}
      >
        <Campo
          rotulo="O que precisa ser feito"
          value={titulo}
          maxLength={500}
          onChange={(e) => definirTitulo(e.target.value)}
        />

        <CampoArea
          rotulo="Detalhes"
          value={corpo}
          rows={3}
          maxLength={20_000}
          onChange={(e) => definirCorpo(e.target.value)}
        />

        <div className="tar__form-linha">
          <Campo
            rotulo="Prazo"
            type="datetime-local"
            value={prazo}
            onChange={(e) => definirPrazo(e.target.value)}
          />
          <CampoSelecao
            rotulo="Prioridade"
            value={prioridade}
            onChange={(e) => definirPrioridade(e.target.value as PrioridadeDaTarefa)}
          >
            {PRIORIDADES.map(({ valor, nome }) => (
              <option key={valor} value={valor}>
                {nome}
              </option>
            ))}
          </CampoSelecao>
          <CampoSelecao
            rotulo="Responsável"
            value={responsavel}
            onChange={(e) => definirResponsavel(e.target.value)}
          >
            <option value="">Sem dono</option>
            {(equipe.data?.itens ?? []).map((pessoa) => (
              <option
                key={pessoa.referencia_externa ?? pessoa.nome}
                value={pessoa.referencia_externa ?? ''}
                disabled={!pessoa.pode_receber}
              >
                {pessoa.nome}
              </option>
            ))}
          </CampoSelecao>
        </div>

        <div className="tar__acoes-detalhe tar__acoes-detalhe--form">
          <Botao
            type="submit"
            variante="primario"
            desabilitado={titulo.trim() === '' || salvar.isPending}
            motivo={titulo.trim() === '' ? 'Escreva o que precisa ser feito.' : 'Salvando…'}
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Botao>
          <Botao variante="fantasma" onClick={aoSair}>
            Cancelar
          </Botao>
        </div>
      </form>
    </div>
  );
}

/**
 * Só o que MUDOU.
 *
 * O `PATCH` trata campo ausente como "não mexa" — mandar o objeto inteiro
 * gravaria de novo o que ninguém tocou, e faria a engine emitir um evento de
 * atualização que a projeção processaria à toa. Pior: se um campo chegasse
 * aqui com o valor errado por causa de uma corrida, ele seria salvo por cima.
 */
function diferenca(
  antes: TarefaDaApi,
  depois: {
    titulo: string;
    corpo: string | null;
    prazo_em: string | null;
    prioridade: PrioridadeDaTarefa;
    responsavel_referencia_externa: string | null;
  },
): Record<string, unknown> {
  const mudou: Record<string, unknown> = {};
  if (depois.titulo !== antes.titulo) mudou['titulo'] = depois.titulo;
  if (depois.corpo !== antes.corpo) mudou['corpo'] = depois.corpo;
  if (!mesmoInstante(depois.prazo_em, antes.prazo_em)) mudou['prazo_em'] = depois.prazo_em;
  if (depois.prioridade !== antes.prioridade) mudou['prioridade'] = depois.prioridade;
  if (depois.responsavel_referencia_externa !== antes.responsavel_referencia_externa) {
    mudou['responsavel_referencia_externa'] = depois.responsavel_referencia_externa;
  }
  return mudou;
}

/**
 * Compara INSTANTES, e não as strings.
 *
 * O campo `datetime-local` não tem segundos nem fuso, então reabrir e salvar
 * sem tocar no prazo produziria `2026-09-15T09:00:00.000Z` contra
 * `2026-09-15T09:00:00Z` — textos diferentes para o mesmo momento, e um
 * `PATCH` que grava o que ninguém mudou.
 */
function mesmoInstante(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b;
  return Date.parse(a) === Date.parse(b);
}

/**
 * Instante → o valor que o `datetime-local` entende.
 *
 * Ele trabalha na hora LOCAL sem fuso, então o `toISOString` não serve: ele
 * devolveria UTC e o campo mostraria três horas a menos em São Paulo.
 */
function paraCampoLocal(instante: string | null): string {
  if (!instante) return '';
  const data = new Date(instante);
  if (Number.isNaN(data.getTime())) return '';
  const doisDigitos = (n: number) => String(n).padStart(2, '0');
  return (
    `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}` +
    `T${doisDigitos(data.getHours())}:${doisDigitos(data.getMinutes())}`
  );
}
